import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api';
import type { ExportPreset, ExportPresetFormData } from '../types';

const KEYS = {
  all: ['exportPresets'] as const,
  list: () => [...KEYS.all, 'list'] as const,
};

export function useExportPresets() {
  return useQuery({
    queryKey: KEYS.list(),
    queryFn: async () => {
      const { data } = await adminApi.getExportPresets();
      return data.presets as ExportPreset[];
    },
  });
}

export function useCreateExportPreset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ExportPresetFormData) => {
      const { data: result } = await adminApi.createExportPreset(data);
      return result.preset as ExportPreset;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateExportPreset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ExportPresetFormData> }) => {
      const { data: result } = await adminApi.updateExportPreset(id, data);
      return result.preset as ExportPreset;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteExportPreset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await adminApi.deleteExportPreset(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useSetExportPresetDefault() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: result } = await adminApi.setExportPresetDefault(id);
      return result.preset as ExportPreset;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
